import React, { Component, ErrorInfo, ReactNode } from 'react';
import LogRocket from 'logrocket';
import vercelLogger from '../utils/vercelRuntimeLogger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    const errorId = `error_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    
    return {
      hasError: true,
      errorId,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const sessionInfo = vercelLogger.getSessionInfo();

    // Log error to Vercel logger
    vercelLogger.error('ErrorBoundary', 'REACT_ERROR_BOUNDARY', error, {
      errorInfo: errorInfo.componentStack,
      errorId: this.state.errorId,
      correlationId: this.state.errorId,
      release: sessionInfo.release,
    });

    // Update state with error details
    this.setState({
      error,
      errorInfo,
    });

    // Send error to external monitoring service if available
    if (typeof window !== 'undefined') {
      const componentStack = errorInfo.componentStack || '';
      LogRocket.captureException(error, {
        extra: {
          componentStack,
          errorId: this.state.errorId || '',
          sessionId: sessionInfo.sessionId,
          release: sessionInfo.release,
        },
      });
    }
  }

  handleReset = () => {
    vercelLogger.info('ErrorBoundary', 'ERROR_RESET', {
      errorId: this.state.errorId,
    });

    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100">
          <div className="max-w-2xl w-full mx-4">
            <div className="bg-white rounded-lg shadow-xl p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
                  <svg
                    className="w-6 h-6 text-red-600"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                  </svg>
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    Something went wrong
                  </h1>
                  <p className="text-gray-600 mt-1">
                    An unexpected error occurred in the application
                  </p>
                </div>
              </div>

              {process.env.NODE_ENV === 'development' && this.state.error && (
                <div className="mb-6">
                  <details className="bg-gray-50 rounded-lg p-4">
                    <summary className="cursor-pointer font-medium text-gray-700 hover:text-gray-900">
                      Error Details (Development Only)
                    </summary>
                    <div className="mt-4 space-y-4">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Error ID:</p>
                        <p className="text-xs font-mono text-gray-800">{this.state.errorId}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">Error Message:</p>
                        <p className="text-sm text-red-600 font-mono">
                          {this.state.error.message}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">Stack Trace:</p>
                        <pre className="text-xs text-gray-700 overflow-auto max-h-40 bg-white p-2 rounded border">
                          {this.state.error.stack}
                        </pre>
                      </div>
                      {this.state.errorInfo && (
                        <div>
                          <p className="text-sm font-medium text-gray-600">Component Stack:</p>
                          <pre className="text-xs text-gray-700 overflow-auto max-h-40 bg-white p-2 rounded border">
                            {this.state.errorInfo.componentStack}
                          </pre>
                        </div>
                      )}
                    </div>
                  </details>
                </div>
              )}

              <div className="flex gap-4">
                <button
                  onClick={this.handleReset}
                  className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                >
                  Try Again
                </button>
                <button
                  onClick={() => window.location.href = '/'}
                  className="flex-1 bg-gray-200 text-gray-800 px-6 py-3 rounded-lg font-medium hover:bg-gray-300 transition-colors"
                >
                  Go Home
                </button>
              </div>

              {this.state.errorId && (
                <p className="text-xs text-gray-500 text-center mt-6">
                  Error ID: {this.state.errorId}
                  <br />
                  This error has been automatically reported
                </p>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
